import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Platform", href: "#modules" },
  { label: "Billing", href: "#services" },
  { label: "Free AR Audit", href: "#ar-audit" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact Us", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-[#D8E6F1]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center group">
          <img src="/astra-logo.png" alt="Astra" className="h-11 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A] hover:text-[#0C2A40] hover-underline transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="font-mono text-[11px] tracking-[0.15em] uppercase px-5 py-2.5 rounded-full bg-[#2880B8] text-white hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center gap-1"
            >
              Dashboard <ArrowUpRight size={14} />
            </button>
          ) : (
            <>
              <button
                data-testid="nav-login-btn"
                onClick={() => navigate("/login")}
                className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A] hover:text-[#0C2A40] transition-colors duration-300"
              >
                Log In
              </button>
              <a
                href="#book"
                data-testid="nav-signup-btn"
                className="font-mono text-[11px] tracking-[0.15em] uppercase px-5 py-2.5 rounded-full bg-[#2880B8] text-white hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center gap-1 shadow-[0_8px_24px_-8px_rgba(40,128,184,0.6)]"
              >
                Get a Demo <ArrowUpRight size={14} />
              </a>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-[#0C2A40]"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-[#D8E6F1]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs tracking-[0.15em] uppercase text-[#51667A]"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-4 border-t border-[#D8E6F1]">
                <button onClick={() => navigate("/login")} className="flex-1 py-3 rounded-full border border-[#D8E6F1] font-mono text-xs uppercase tracking-[0.15em] text-[#0C2A40]">Log In</button>
                <a href="#book" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full bg-[#2880B8] text-white font-mono text-xs uppercase tracking-[0.15em] text-center">Get a Demo</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
